/**
 * Response store for managing answers.
 */

import { create } from 'zustand';
import type { EvalValue } from '@diy-platform/logic-eval';
import type {
  AnswerValue,
  AnswerMetadata,
  SubmitAnswerRequest,
  StoredAnswer,
  TerminalOutcome,
} from '@/types';
import { submitAnswer, submitPageAnswers, getResponseData, telemetryService } from '@/lib/api';
import {
  recomputeForAnswerChange,
  type AnswerMap,
} from '@/lib/logic/blocks-evaluator';
import type { LogicRule, ComputeAction, WriteAction } from '@/types';
import { useSessionStore } from './session-store';
import { useSurveyStore } from './survey-store';

/**
 * Determine the value type for telemetry
 */
function getValueType(value: AnswerValue): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  return typeof value;
}

interface ResponseState {
  // Answers keyed by question code
  answers: Record<string, AnswerValue>;

  // Metadata for each answer
  metadata: Record<string, AnswerMetadata>;

  /**
   * Phase 76 — derived/hidden variables produced by compute/write logic
   * blocks. Player evaluation is best-effort (UX preview for piping +
   * conditional display); the BE recomputes authoritatively at session
   * completion and overwrites whatever the player wrote.
   */
  variables: Record<string, EvalValue>;

  // Tracking
  dirtyAnswers: Set<string>;
  lastSavedAt: Date | null;
  isSaving: boolean;

  // Question timing
  questionStartTimes: Record<string, Date>;

  // Actions
  setAnswer: (
    questionCode: string,
    questionId: string,
    value: AnswerValue
  ) => void;
  getVariable: (name: string) => EvalValue | undefined;
  getAnswer: (questionCode: string) => AnswerValue;
  hasAnswer: (questionCode: string) => boolean;
  startQuestion: (questionCode: string, questionId: string) => void;
  getTimeSpent: (questionCode: string) => number;
  setDisplayOrder: (questionCode: string, order: number[]) => void;
  saveAnswer: (questionCode: string) => Promise<TerminalOutcome | null>;
  savePageAnswers: (
    pageNumber: number,
    questionCodes: string[]
  ) => Promise<TerminalOutcome | null>;
  loadExistingResponse: (sessionId: string) => Promise<void>;
  markAsSaved: () => void;
  reset: () => void;
}

export const useResponseStore = create<ResponseState>((set, get) => ({
  // Initial state
  answers: {},
  metadata: {},
  variables: {},
  dirtyAnswers: new Set(),
  lastSavedAt: null,
  isSaving: false,
  questionStartTimes: {},

  // Set an answer
  setAnswer: (questionCode: string, questionId: string, value: AnswerValue) => {
    const oldValue = get().answers[questionCode];

    // Track the answer change in telemetry
    telemetryService.trackAnswerChanged(
      questionCode,
      oldValue,
      value,
      getValueType(value),
    );

    set((state) => {
      const newAnswers = { ...state.answers, [questionCode]: value };
      const newMetadata = {
        ...state.metadata,
        [questionCode]: {
          ...state.metadata[questionCode],
          questionId,
          timeSpent: get().getTimeSpent(questionCode),
          startedAt:
            state.questionStartTimes[questionCode] ||
            state.metadata[questionCode]?.startedAt ||
            new Date(),
        },
      };
      const newDirtyAnswers = new Set(state.dirtyAnswers);
      newDirtyAnswers.add(questionCode);

      // Phase 76 — recompute compute/write variables for piping / display
      // preview. Best-effort: errors are recorded but not surfaced (BE
      // recompute at submit is authoritative). Skips entirely when no
      // compute/write rules are present.
      //
      // Variable handling (review-fix): for the subset of variables OWNED
      // by compute/write blocks, we DROP the previous values and let
      // recompute repopulate from `{}`. This matches BE
      // `LogicBlocksRecomputeService` semantics — otherwise a block that
      // succeeded last tick but fails this tick (e.g. its referenced
      // answer got cleared) would leave its stale value in the store and
      // disagree with the authoritative result.
      const rules = useSurveyStore.getState().logicRules;
      const blockOwnedNames = collectBlockOwnedVariableNames(rules);
      const hasBlocks = blockOwnedNames.size > 0;

      let newVariables: Record<string, EvalValue>;
      if (hasBlocks) {
        // Preserve any non-block variables (none in v0.1, but future-
        // proofing) while always recomputing block-owned ones fresh.
        const preserved: Record<string, EvalValue> = {};
        for (const [name, value] of Object.entries(state.variables)) {
          if (!blockOwnedNames.has(name)) preserved[name] = value;
        }
        const recomputed = recomputeForAnswerChange(
          questionCode,
          rules,
          toEvalAnswers(newAnswers),
        ).variables;
        newVariables = { ...preserved, ...recomputed };
      } else {
        newVariables = state.variables;
      }

      return {
        answers: newAnswers,
        metadata: newMetadata,
        dirtyAnswers: newDirtyAnswers,
        variables: newVariables,
      };
    });
  },

  // Read a variable computed by a compute/write block.
  getVariable: (name: string) => {
    return get().variables[name];
  },

  // Get an answer
  getAnswer: (questionCode: string) => {
    return get().answers[questionCode] ?? null;
  },

  // Check if question has an answer
  hasAnswer: (questionCode: string) => {
    const answer = get().answers[questionCode];
    if (answer === null || answer === undefined) return false;
    if (typeof answer === 'string') return answer.trim().length > 0;
    if (Array.isArray(answer)) return answer.length > 0;
    if (typeof answer === 'object') return Object.keys(answer).length > 0;
    return true;
  },

  // Start timing a question
  startQuestion: (questionCode: string, questionId: string) => {
    set((state) => ({
      questionStartTimes: {
        ...state.questionStartTimes,
        [questionCode]: new Date(),
      },
      metadata: {
        ...state.metadata,
        [questionCode]: {
          ...state.metadata[questionCode],
          questionId,
          startedAt: new Date(),
          timeSpent: 0,
        },
      },
    }));
  },

  // Get time spent on a question in milliseconds
  getTimeSpent: (questionCode: string) => {
    const startTime = get().questionStartTimes[questionCode];
    if (!startTime) return 0;
    return Date.now() - startTime.getTime();
  },

  // Set display order for randomization tracking
  setDisplayOrder: (questionCode: string, order: number[]) => {
    set((state) => ({
      metadata: {
        ...state.metadata,
        [questionCode]: {
          ...state.metadata[questionCode],
          displayOrder: order,
        },
      },
    }));
  },

  // Save a single answer
  saveAnswer: async (questionCode: string) => {
    const { answers, metadata, dirtyAnswers } = get();
    const sessionId = useSessionStore.getState().sessionId;

    if (!sessionId || !dirtyAnswers.has(questionCode)) return null;

    const value = answers[questionCode];
    const meta = metadata[questionCode];

    if (value === undefined || !meta) return null;

    set({ isSaving: true });

    try {
      const request: SubmitAnswerRequest = {
        questionCode,
        questionId: meta.questionId,
        value,
        timeSpent: meta.timeSpent,
        displayOrder: meta.displayOrder,
      };

      const response = await submitAnswer(sessionId, request);

      set((state) => {
        const newDirtyAnswers = new Set(state.dirtyAnswers);
        newDirtyAnswers.delete(questionCode);
        return {
          dirtyAnswers: newDirtyAnswers,
          lastSavedAt: new Date(),
          isSaving: false,
        };
      });

      // BE ended the session (overquota/timeout/etc): end locally so the
      // player redirects instead of continuing.
      if (response.terminal) {
        useSessionStore.getState().applyTerminal(response.terminal);
        return response.terminal;
      }
      return null;
    } catch (error) {
      set({ isSaving: false });
      throw error;
    }
  },

  // Save all answers for a page
  savePageAnswers: async (pageNumber: number, questionCodes: string[]) => {
    const { answers, metadata, dirtyAnswers } = get();
    const sessionId = useSessionStore.getState().sessionId;

    if (!sessionId) return null;

    // Filter to only dirty answers on this page. We submit even when this is
    // empty: a clean-page Next must still hit the server so an already-expired
    // session returns a terminal (otherwise navigation would advance / complete
    // against a dead session and the respondent gets stuck).
    const answersToSave = questionCodes.filter((code) =>
      dirtyAnswers.has(code)
    );

    set({ isSaving: true });

    try {
      const pageAnswers: SubmitAnswerRequest[] = answersToSave
        .map((questionCode): SubmitAnswerRequest | null => {
          const value = answers[questionCode];
          const meta = metadata[questionCode];

          if (value === undefined || !meta) return null;

          return {
            questionCode,
            questionId: meta.questionId,
            value,
            timeSpent: meta.timeSpent,
            displayOrder: meta.displayOrder,
          };
        })
        .filter((a): a is SubmitAnswerRequest => a !== null);

      const response = await submitPageAnswers(sessionId, {
        pageNumber,
        answers: pageAnswers,
      });

      set((state) => {
        const newDirtyAnswers = new Set(state.dirtyAnswers);
        answersToSave.forEach((code) => newDirtyAnswers.delete(code));
        return {
          dirtyAnswers: newDirtyAnswers,
          lastSavedAt: new Date(),
          isSaving: false,
        };
      });

      // BE ended the session: end locally and signal the caller to stop before
      // advancing to the next page.
      if (response.terminal) {
        useSessionStore.getState().applyTerminal(response.terminal);
        return response.terminal;
      }
      return null;
    } catch (error) {
      set({ isSaving: false });
      throw error;
    }
  },

  // Load existing response for session recovery
  loadExistingResponse: async (sessionId: string) => {
    try {
      const response = await getResponseData(sessionId);

      if (response && response.answers) {
        const answers: Record<string, AnswerValue> = {};
        const metadata: Record<string, AnswerMetadata> = {};

        Object.entries(response.answers).forEach(
          ([code, stored]: [string, StoredAnswer]) => {
            answers[code] = stored.value;
            metadata[code] = {
              questionId: stored.questionId,
              timeSpent: stored.timeSpent || 0,
              startedAt: new Date(stored.answeredAt),
              displayOrder: stored.displayOrder,
            };
          }
        );

        set({
          answers,
          metadata,
          dirtyAnswers: new Set(),
          lastSavedAt: new Date(),
        });
      }
    } catch {
      // No existing response, start fresh
    }
  },

  // Mark all as saved
  markAsSaved: () => {
    set({
      dirtyAnswers: new Set(),
      lastSavedAt: new Date(),
    });
  },

  // Reset store
  reset: () => {
    set({
      answers: {},
      metadata: {},
      variables: {},
      dirtyAnswers: new Set(),
      lastSavedAt: null,
      isSaving: false,
      questionStartTimes: {},
    });
  },
}));

/**
 * Narrow the Player's AnswerValue (which can include nested matrix /
 * file-upload shapes) down to what the shared evaluator accepts. Matches
 * the BE recompute service's normalization (matrix/file values → null;
 * v0.1 expression subset doesn't reach into those structures anyway).
 */
function toEvalAnswers(answers: Record<string, AnswerValue>): AnswerMap {
  const out: Record<string, EvalValue | EvalValue[]> = {};
  for (const [code, value] of Object.entries(answers)) {
    out[code] = normalizeAnswer(value);
  }
  return out;
}

function collectBlockOwnedVariableNames(rules: LogicRule[]): Set<string> {
  const names = new Set<string>();
  for (const r of rules) {
    if (r.type !== 'compute' && r.type !== 'write') continue;
    const action = r.action as ComputeAction | WriteAction | undefined;
    if (action && typeof action.variable === 'string') {
      names.add(action.variable);
    }
  }
  return names;
}

function normalizeAnswer(v: AnswerValue): EvalValue | EvalValue[] {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return v;
  }
  if (Array.isArray(v)) {
    const out: EvalValue[] = [];
    for (const x of v) {
      if (
        typeof x === 'string' ||
        typeof x === 'number' ||
        typeof x === 'boolean'
      ) {
        out.push(x);
      }
    }
    return out;
  }
  return null;
}
